<template>
  <form @submit.prevent="$emit('submit')">
    <FormInputGroup label="Name" for-id="name">
      <FormInput
        id="name"
        ref="nameInput"
        v-model:value="inputName"
        type="text"
        placeholder="Type a name..."
        :state="nameMissing || nameDuplicate ? 'error' : 'default'"
        @update:value="setNameFilled"
        @keydown="handleKeydown"
      />
      <BaseAlert v-if="nameMissing" state="error">
        Name is required.
      </BaseAlert>
      <BaseAlert v-if="nameDuplicate" state="error">
        A field with this name already exists in this type.
      </BaseAlert>
    </FormInputGroup>

    <CollapsibleSection header="Advanced">
      <FormInputGroup label="Description" for-id="description">
        <FormInput
          id="description"
          v-model:value="inputDescription"
          type="text"
          placeholder="Describe your property..."
          state="default"
        />
      </FormInputGroup>

      <FormInputGroup label="Default" for-id="default">
        <FormInput
          v-if="
            type === 'Text' ||
            type === 'Email' ||
            type === 'Phone' ||
            type === 'URL' ||
            type === 'Number'
          "
          id="default"
          v-model:value="defaultValue"
          :type="type === 'Number' ? 'number' : 'text'"
          placeholder="Enter a default value..."
          state="disabled"
        />
        <BaseToggleButton
          v-else-if="type === 'Boolean'"
          :checked="false"
          disabled
        />
      </FormInputGroup>

      <FormInputGroup
        v-if="type !== 'Boolean'"
        label="Constraints"
        for-id="constraints"
      >
        <BaseToggleButton
          label="Required"
          help="This field is required"
          :checked="required"
          @change="(e) => toggle(e)"
        />
      </FormInputGroup>
    </CollapsibleSection>
  </form>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import CollapsibleSection from "@/components/graph/definition/form/CollapsibleSection.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import FormInputGroup from "@/components/graph/definition/form/FormInputGroup.vue";
import { Filled } from "@/components/settings/TypeEdition.vue";

export default defineComponent({
  components: {
    BaseAlert,
    CollapsibleSection,
    FormInput,
    FormInputGroup,
    BaseToggleButton,
  },
  props: {
    name: { type: String, required: true },
    description: { type: String, default: "" },
    type: {
      type: String as PropType<
        | "Text"
        | "Email"
        | "Phone"
        | "URL"
        | "Number"
        | "Date"
        | "Boolean"
        | "Notes"
        | "Context"
      >,
    },
    filled: { type: Object as PropType<Filled>, required: true },
    optional: { type: Boolean, required: true },
    existingNames: { type: Array as PropType<string[]>, default: () => [] },
  },
  emits: {
    updateName: (_name: string) => true,
    updateDescription: (_description: string) => true,
    setNameFilled: () => true,
    updateOptional: (_optional: boolean) => true,
    submit: () => true,
  },
  setup(props, { emit }) {
    const { filled, name, description, optional, existingNames } =
      toRefs(props);
    const nameMissing = computed<boolean>(() => !filled.value.name);
    const nameDuplicate = computed<boolean>(() =>
      name.value ? existingNames.value.includes(name.value) : false
    );
    const setNameFilled = () => emit("setNameFilled");
    const nameInput = ref<InstanceType<typeof FormInput>>();

    onMounted(() => {
      nameInput.value?.focus();
    });

    const inputName = computed({
      get() {
        return name.value;
      },
      set(newName: string) {
        emit("updateName", newName);
      },
    });
    const inputDescription = computed({
      get() {
        return description.value;
      },
      set(newDesc: string) {
        emit("updateDescription", newDesc);
      },
    });
    const defaultValue = ref("");
    const required = computed<boolean>({
      get() {
        return !optional.value;
      },
      set(required: boolean) {
        emit("updateOptional", !required);
      },
    });
    const toggle = (requiredStatus: boolean) => {
      required.value = requiredStatus;
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        emit("submit");
      }
    };

    return {
      defaultValue,
      inputDescription,
      inputName,
      required,
      setNameFilled,
      nameMissing,
      nameDuplicate,
      toggle,
      nameInput,
      handleKeydown,
    };
  },
});
</script>

<style lang="css" scoped>
form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
